import { z } from 'zod';
import { UserSummary } from './admin';
import {
  LlmStatus,
  LlmTier,
  ResultClass,
  SerpFeatures,
  SerpItemType,
  SerpStatus,
} from './cluster-enrichment';
import { ClusterMetrics, ClusterStage, EligibilityReason, EligibilityStatus } from './clusters';
import { Id, IsoDate, PaginationQuery, paginated, queryArray, SortOrder } from './common';
import { Intent, ProductType, SCORE_AXES, ScoreAxis } from './enums';
import { ErrorCode } from './errors';
import { KeywordRow } from './keywords';
import { normalizeTags } from './seeds';
import { ExperimentStatus, OpportunityStatus, RejectionReason, Verdict } from './transitions';

/**
 * Contrats des opportunités (`api.md` § Opportunités, fiche T22). Les statuts et raisons de rejet
 * viennent de `transitions.ts` ; les charges `jsonb` gardent leurs clés `snake_case` telles que
 * stockées (`data-model.md` § `opportunities`).
 */

// ---------------------------------------------------------------------------
// Énumérations (mêmes valeurs que les enums Postgres)
// ---------------------------------------------------------------------------

// `Intent`, `ProductType`, `SCORE_AXES` et `ScoreAxis` sont définis une seule fois dans `enums.ts`
// et ré-exportés ici pour que les appelants n'aient qu'un point d'entrée par ressource.
// Types SERP partagés avec `cluster-enrichment.ts` (définis une seule fois là-bas).
export {
  Intent,
  ProductType,
  ResultClass,
  SCORE_AXES,
  ScoreAxis,
  SerpFeatures,
  SerpItemType,
  SerpStatus,
};

export const OpportunitySource = z.enum(['pipeline', 'manual']);
export type OpportunitySource = z.infer<typeof OpportunitySource>;

export const Severity = z.enum(['low', 'medium', 'high']);
export type Severity = z.infer<typeof Severity>;

export const ConfidenceBand = z.enum(['low', 'medium', 'high']);
export type ConfidenceBand = z.infer<typeof ConfidenceBand>;

export const PricingModel = z.enum(['subscription', 'usage', 'one_time', 'freemium', 'unknown']);
export type PricingModel = z.infer<typeof PricingModel>;

/** Cibles d'override : un axe A–G ou le type de produit (`score_overrides.axis`). */
export const OverrideAxis = z.enum([...SCORE_AXES, 'product_type']);
export type OverrideAxis = z.infer<typeof OverrideAxis>;

export const OpportunityEventType = z.enum([
  'created',
  'status_changed',
  'comment',
  'score_override',
  'override_disabled',
  'assigned',
  'rescored',
  'merged',
  'field_edited',
  'experiment_created',
  'experiment_updated',
  'experiment_result',
  'outcome_updated',
  'reanalyzed',
  'serp_refreshed',
]);
export type OpportunityEventType = z.infer<typeof OpportunityEventType>;

// ---------------------------------------------------------------------------
// Charges jsonb (clés snake_case, formes de `data-model.md`)
// ---------------------------------------------------------------------------

const Refs = z.array(z.string().max(80)).max(30);

export const DominantSolution = z.object({
  name: z.string().max(200),
  domain: z.string().max(200),
  type: ResultClass,
  weaknesses: z.array(z.string().max(300)).max(10),
  refs: Refs,
});
export type DominantSolution = z.infer<typeof DominantSolution>;

export const UnmetNeed = z.object({ need: z.string().max(300), refs: Refs });
export type UnmetNeed = z.infer<typeof UnmetNeed>;

export const CurrentOffer = z.object({
  summary: z.string().max(2000),
  dominant_solutions: z.array(DominantSolution).max(12),
  unmet_needs: z.array(UnmetNeed).max(12),
});
export type CurrentOffer = z.infer<typeof CurrentOffer>;

export const ProductHypothesis = z.object({
  value_proposition: z.string().max(1000),
  wedge: z.string().max(1000),
  key_features: z.array(z.string().max(200)).max(12),
  dependencies: z.array(z.string().max(200)).max(12),
});
export type ProductHypothesis = z.infer<typeof ProductHypothesis>;

export const PricingHypothesis = z.object({
  model: PricingModel,
  price_point: z.string().max(200).nullable(),
  rationale: z.string().max(1000),
  refs: Refs,
});
export type PricingHypothesis = z.infer<typeof PricingHypothesis>;

export const Economics = z.object({
  pricing_hypothesis: PricingHypothesis,
  cac_hypothesis: z.string().max(1000),
  revenue_hypothesis: z.string().max(1000),
});
export type Economics = z.infer<typeof Economics>;

export const Distribution = z.object({
  ads_viability: Severity,
  seo_viability: Severity,
  other_channels: z.array(z.string().max(200)).max(10),
});
export type Distribution = z.infer<typeof Distribution>;

export const OpportunityEvidence = z.object({
  id: z.string(),
  claim: z.string(),
  refs: z.array(z.string()),
});
export type OpportunityEvidence = z.infer<typeof OpportunityEvidence>;

export const OpportunityUnknown = z.object({
  id: z.string(),
  question: z.string(),
  blocking: z.boolean(),
  resolved_at: z.string().nullable(),
  resolution: z.string().nullable(),
});
export type OpportunityUnknown = z.infer<typeof OpportunityUnknown>;

export const OpportunityRisk = z.object({ risk: z.string(), severity: Severity });
export type OpportunityRisk = z.infer<typeof OpportunityRisk>;

/** `opportunities.scores` et `reviewed_scores` (`scoring.md` § 4–6, produit par `scoreOpportunity`). */
export const OpportunityScores = z.object({
  config_version: z.number().int(),
  axes: z.record(ScoreAxis, z.number()),
  intermediates: z.record(z.string(), z.number().nullable()),
  attractiveness: z.number(),
  confidence: z.number(),
  confidence_parts: z.record(z.string(), z.number()),
  confidence_band: ConfidenceBand,
  computed_at: z.string(),
});
export type OpportunityScores = z.infer<typeof OpportunityScores>;

/** `clusters.supply` (`scoring.md` § 1), présent après enrichissement SERP. */
export const ClusterSupply = z.object({
  serp_count: z.number().int(),
  tool_ratio: z.number(),
  content_ratio: z.number(),
  ugc_ratio: z.number(),
  marketplace_ratio: z.number(),
  big_brand_ratio: z.number(),
  domain_concentration: z.number(),
  ads_top_avg: z.number(),
  ads_any_ratio: z.number(),
  paa_count: z.number(),
  overlap_within: z.number().nullable(),
  computed_at: z.string().optional(),
});
export type ClusterSupply = z.infer<typeof ClusterSupply>;

// ---------------------------------------------------------------------------
// Liste (`GET /opportunities`, lignes de `v_opportunity_list`)
// ---------------------------------------------------------------------------

export const OPPORTUNITY_SORT_FIELDS = [
  'attractiveness',
  'reviewedAttractiveness',
  'confidence',
  'volumeSum',
  'bidHigh',
  'updatedAt',
  'statusChangedAt',
  'manualRank',
] as const;
export const OpportunitySort = z.enum(OPPORTUNITY_SORT_FIELDS);
export type OpportunitySort = z.infer<typeof OpportunitySort>;

/** `ownerId=none` : opportunités non assignées. */
export const OWNER_NONE = 'none';

/** Date ISO complète ou jour seul (`2026-09-01`). */
const DateInput = z.union([IsoDate, z.iso.date()]);

export const OpportunitiesQuery = PaginationQuery.extend({
  marketId: Id.optional(),
  status: queryArray(OpportunityStatus).optional(),
  /** Bornes sur le score revu (`reviewed_scores`, sinon `scores`). */
  minScore: z.coerce.number().min(0).max(100).optional(),
  maxScore: z.coerce.number().min(0).max(100).optional(),
  minConfidence: z.coerce.number().min(0).max(1).optional(),
  productType: queryArray(ProductType).optional(),
  /** Id d'utilisateur, ou `none` pour « non assignée ». */
  ownerId: z.string().trim().min(1).optional(),
  /** Au moins un tag en commun (`&&`). */
  tags: queryArray(z.string().trim().min(1).max(60)).optional(),
  /** Recherche `ILIKE` sur `title` et `target_segment`. */
  q: z.string().trim().min(1).max(200).optional(),
  updatedSince: DateInput.optional(),
  sort: OpportunitySort.default('reviewedAttractiveness'),
  order: SortOrder.default('desc'),
});
export type OpportunitiesQuery = z.infer<typeof OpportunitiesQuery>;
export type OpportunitiesQueryInput = z.input<typeof OpportunitiesQuery>;

export const OpportunityRow = z.object({
  id: Id,
  marketId: z.string(),
  clusterId: Id.nullable(),
  source: OpportunitySource,
  status: OpportunityStatus,
  title: z.string(),
  targetSegment: z.string(),
  intent: Intent,
  productType: ProductType,
  owner: UserSummary.nullable(),
  tags: z.array(z.string()),
  manualRank: z.number().int().nullable(),
  /** Score système ; `null` tant qu'aucun score n'est calculé. */
  attractiveness: z.number().nullable(),
  /** Score revu (`reviewed_scores`), `null` sans override actif. */
  reviewedAttractiveness: z.number().nullable(),
  confidence: z.number().nullable(),
  scoresConfigVersion: z.number().int().nullable(),
  volumeSum: z.number().nullable(),
  bidHighUsd: z.number().nullable(),
  keywordCount: z.number().int().nullable(),
  isHeterogeneous: z.boolean(),
  softFlags: z.array(z.string()),
  commentCount: z.number().int(),
  blockingUnknowns: z.number().int(),
  lastEventType: OpportunityEventType.nullable(),
  lastEventAt: IsoDate.nullable(),
  parkUntil: IsoDate.nullable(),
  statusChangedAt: IsoDate,
  updatedAt: IsoDate,
  createdAt: IsoDate,
});
export type OpportunityRow = z.infer<typeof OpportunityRow>;

export const OpportunitiesResponse = paginated(OpportunityRow);
export type OpportunitiesResponse = z.infer<typeof OpportunitiesResponse>;

// ---------------------------------------------------------------------------
// Fiche (`GET /opportunities/:id`)
// ---------------------------------------------------------------------------

/** Toutes les colonnes de `opportunities`. */
export const Opportunity = z.object({
  id: Id,
  clusterId: Id.nullable(),
  marketId: z.string(),
  source: OpportunitySource,
  title: z.string(),
  canonicalJob: z.string(),
  targetSegment: z.string(),
  secondarySegments: z.array(z.string()),
  context: z.string().nullable(),
  intent: Intent,
  productType: ProductType,
  currentOffer: CurrentOffer,
  productHypothesis: ProductHypothesis,
  economics: Economics,
  distribution: Distribution,
  scores: OpportunityScores,
  reviewedScores: OpportunityScores.nullable(),
  evidence: z.array(OpportunityEvidence),
  unknowns: z.array(OpportunityUnknown),
  risks: z.array(OpportunityRisk),
  recommendedNextStep: z.string().nullable(),
  status: OpportunityStatus,
  previousStatus: OpportunityStatus.nullable(),
  statusChangedAt: IsoDate,
  ownerId: Id.nullable(),
  tags: z.array(z.string()),
  manualRank: z.number().int().nullable(),
  mergedIntoId: Id.nullable(),
  lastAnalysisId: Id.nullable(),
  parkUntil: IsoDate.nullable(),
  createdAt: IsoDate,
  updatedAt: IsoDate,
});
export type Opportunity = z.infer<typeof Opportunity>;

export const OpportunityCluster = z.object({
  id: Id,
  label: z.string(),
  stage: ClusterStage,
  eligibilityStatus: EligibilityStatus,
  eligibilityReasons: z.array(EligibilityReason),
  priorityScore: z.number().nullable(),
  triageScore: z.number().nullable(),
  keywordCount: z.number().int(),
  metrics: ClusterMetrics.nullable(),
  supply: ClusterSupply.nullable(),
  isHeterogeneous: z.boolean(),
  softFlags: z.array(z.string()),
  mergedIntoClusterId: Id.nullable(),
});
export type OpportunityCluster = z.infer<typeof OpportunityCluster>;

export const SerpItem = z.object({
  id: z.number().int(),
  type: SerpItemType,
  rankGroup: z.number().int(),
  rankAbsolute: z.number().int(),
  domain: z.string().nullable(),
  url: z.string().nullable(),
  title: z.string().nullable(),
  description: z.string().nullable(),
  classification: ResultClass.nullable(),
  classificationSource: z.string().nullable(),
});
export type SerpItem = z.infer<typeof SerpItem>;

export const SerpSnapshot = z.object({
  id: Id,
  keywordId: z.number().int(),
  keywordText: z.string(),
  clusterId: Id.nullable(),
  scanId: Id.nullable(),
  provider: z.string(),
  /** `standard`, `live`. */
  mode: z.string(),
  device: z.string(),
  status: SerpStatus,
  fetchedAt: IsoDate.nullable(),
  seResultsCount: z.number().nullable(),
  itemTypes: z.array(z.string()),
  organicCount: z.number().int(),
  paidCount: z.number().int(),
  paidTopCount: z.number().int(),
  features: SerpFeatures,
  error: z.string().nullable(),
  createdAt: IsoDate,
  items: z.array(SerpItem),
});
export type SerpSnapshot = z.infer<typeof SerpSnapshot>;

/** Métadonnées d'une analyse LLM (liste de la fiche). */
export const AnalysisSummary = z.object({
  id: Id,
  clusterId: Id,
  scanId: Id.nullable(),
  tier: LlmTier,
  model: z.string(),
  promptVersion: z.string(),
  status: LlmStatus,
  costUsd: z.number(),
  latencyMs: z.number().int(),
  error: z.string().nullable(),
  createdAt: IsoDate,
});
export type AnalysisSummary = z.infer<typeof AnalysisSummary>;

/** `GET /opportunities/:id/analyses/:analysisId` : entrée (pack de preuves) et sortie complètes. */
export const AnalysisDetail = AnalysisSummary.extend({
  inputHash: z.string(),
  input: z.record(z.string(), z.unknown()),
  output: z.record(z.string(), z.unknown()).nullable(),
  rawOutput: z.string().nullable(),
  usage: z.object({
    input_tokens: z.number(),
    cached_tokens: z.number(),
    output_tokens: z.number(),
  }),
});
export type AnalysisDetail = z.infer<typeof AnalysisDetail>;

export const OverrideRow = z.object({
  id: Id,
  axis: OverrideAxis,
  /** Valeur système au moment de l'override (0–1). */
  systemValue: z.number(),
  /** Valeur d'override stockée (0–1 ; l'UI saisit 0–10). */
  overrideValue: z.number(),
  rationale: z.string(),
  actor: UserSummary,
  isActive: z.boolean(),
  disabledBy: UserSummary.nullable(),
  disabledAt: IsoDate.nullable(),
  disabledRationale: z.string().nullable(),
  createdAt: IsoDate,
});
export type OverrideRow = z.infer<typeof OverrideRow>;

/** Résumé d'une expérience (le détail est servi par `GET /experiments/:id`, T26). */
export const OpportunityExperiment = z.object({
  id: Id,
  name: z.string(),
  status: ExperimentStatus,
  channel: z.string(),
  budgetPlannedUsd: z.number(),
  budgetSpentUsd: z.number().nullable(),
  verdict: Verdict.nullable(),
  plannedStart: z.string().nullable(),
  plannedEnd: z.string().nullable(),
  startedAt: IsoDate.nullable(),
  endedAt: IsoDate.nullable(),
  decidedAt: IsoDate.nullable(),
  createdAt: IsoDate,
  updatedAt: IsoDate,
});
export type OpportunityExperiment = z.infer<typeof OpportunityExperiment>;

export const OutcomeMetric = z.object({
  metric: z.string(),
  value: z.number(),
  period: z.string(),
});
export type OutcomeMetric = z.infer<typeof OutcomeMetric>;

export const ProductOutcome = z.object({
  id: Id,
  buildStartedAt: IsoDate.nullable(),
  launchedAt: IsoDate.nullable(),
  buildDays: z.number().nullable(),
  buildCostUsd: z.number().nullable(),
  activation: z.array(OutcomeMetric),
  retention: z.array(OutcomeMetric),
  acquisition: z.array(OutcomeMetric),
  revenue: z.array(OutcomeMetric),
  notes: z.string().nullable(),
  updatedBy: UserSummary.nullable(),
  updatedAt: IsoDate,
});
export type ProductOutcome = z.infer<typeof ProductOutcome>;

export const OpportunityEvent = z.object({
  id: Id,
  opportunityId: Id,
  type: OpportunityEventType,
  /** `null` = système (workflow, cron). */
  actor: UserSummary.nullable(),
  /** Forme selon `type` (`data-model.md` § `opportunity_events`). */
  data: z.record(z.string(), z.unknown()),
  rationale: z.string().nullable(),
  createdAt: IsoDate,
});
export type OpportunityEvent = z.infer<typeof OpportunityEvent>;

export const OpportunityEventsQuery = PaginationQuery;
export type OpportunityEventsQuery = z.infer<typeof OpportunityEventsQuery>;
export const OpportunityEventsResponse = paginated(OpportunityEvent);
export type OpportunityEventsResponse = z.infer<typeof OpportunityEventsResponse>;

export const OpportunityMergedInto = z.object({
  id: Id,
  title: z.string(),
  status: OpportunityStatus,
});
export type OpportunityMergedInto = z.infer<typeof OpportunityMergedInto>;

export const OPPORTUNITY_DETAIL_KEYWORDS = 20;
export const OPPORTUNITY_DETAIL_EVENTS = 10;
export const OPPORTUNITY_DETAIL_SNAPSHOTS = 10;

export const OpportunityDetail = z.object({
  opportunity: Opportunity,
  cluster: OpportunityCluster.nullable(),
  /** 20 premiers mots-clés primaires du cluster par volume, et total. */
  keywords: z.object({ items: z.array(KeywordRow), total: z.number().int() }),
  /** Snapshots SERP des clusters rattachés (10 plus récents), avec leurs items. */
  snapshots: z.array(SerpSnapshot),
  analyses: z.array(AnalysisSummary),
  /** Overrides actifs et désactivés, plus récent d'abord. */
  overrides: z.array(OverrideRow),
  experiments: z.array(OpportunityExperiment),
  outcome: ProductOutcome.nullable(),
  owner: UserSummary.nullable(),
  mergedInto: OpportunityMergedInto.nullable(),
  /** 10 derniers événements ; la liste complète est paginée par `GET /opportunities/:id/events`. */
  eventsPreview: z.array(OpportunityEvent),
});
export type OpportunityDetail = z.infer<typeof OpportunityDetail>;

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export const OPPORTUNITY_TITLE_MAX_LENGTH = 200;
export const OPPORTUNITY_JOB_MAX_LENGTH = 400;
export const OPPORTUNITY_SEGMENT_MAX_LENGTH = 200;
export const OPPORTUNITY_TAGS_MAX = 20;
export const OPPORTUNITY_TAG_MAX_LENGTH = 40;
export const RATIONALE_MAX_LENGTH = 2000;
export const OVERRIDE_RATIONALE_MIN_LENGTH = 10;
export const COMMENT_MAX_LENGTH = 4000;
export const BULK_MAX_IDS = 100;

const Title = z.string().trim().min(1).max(OPPORTUNITY_TITLE_MAX_LENGTH);
const Job = z.string().trim().min(1).max(OPPORTUNITY_JOB_MAX_LENGTH);
const Segment = z.string().trim().min(1).max(OPPORTUNITY_SEGMENT_MAX_LENGTH);
const Rationale = z.string().trim().min(1).max(RATIONALE_MAX_LENGTH);
const Tags = z
  .array(z.string().trim().max(OPPORTUNITY_TAG_MAX_LENGTH))
  .max(OPPORTUNITY_TAGS_MAX)
  .transform(normalizeTags);

/** `PATCH /opportunities/:id` : champs éditables (`api.md`) ; un événement `field_edited` par champ modifié. */
export const OpportunityPatch = z.object({
  title: Title.optional(),
  canonicalJob: Job.optional(),
  targetSegment: Segment.optional(),
  secondarySegments: z.array(Segment).max(8).optional(),
  context: z.string().trim().max(2000).nullable().optional(),
  productType: ProductType.optional(),
  currentOffer: CurrentOffer.optional(),
  productHypothesis: ProductHypothesis.optional(),
  economics: Economics.optional(),
  distribution: Distribution.optional(),
  /** Sans événement. */
  tags: Tags.optional(),
  /** Sans événement. */
  manualRank: z.number().int().min(0).nullable().optional(),
  recommendedNextStep: z.string().trim().max(1000).nullable().optional(),
});
export type OpportunityPatch = z.infer<typeof OpportunityPatch>;
export type OpportunityPatchInput = z.input<typeof OpportunityPatch>;

/** `POST /opportunities/:id/status` : champs conditionnels selon la cible (`user-workflow.md`). */
export const StatusChange = z.object({
  to: OpportunityStatus,
  rationale: Rationale.optional(),
  /** Requis pour `rejected`. */
  reason: RejectionReason.optional(),
  /** Requis pour `merged`. */
  mergeIntoId: Id.optional(),
  /** Date de rappel optionnelle pour `parked`. */
  parkUntil: DateInput.optional(),
  /** Date de lancement pour `building → launched`. */
  launchedAt: DateInput.optional(),
});
export type StatusChange = z.infer<typeof StatusChange>;

export const BulkStatusBody = z.object({
  ids: z.array(Id).min(1).max(BULK_MAX_IDS),
  to: OpportunityStatus,
  rationale: Rationale.optional(),
  reason: RejectionReason.optional(),
});
export type BulkStatusBody = z.infer<typeof BulkStatusBody>;

export const BulkAssignBody = z.object({
  ids: z.array(Id).min(1).max(BULK_MAX_IDS),
  ownerId: Id.nullable(),
});
export type BulkAssignBody = z.infer<typeof BulkAssignBody>;

export const BulkItemResult = z.object({
  id: Id,
  ok: z.boolean(),
  error: z.object({ code: ErrorCode, message: z.string() }).optional(),
});
export type BulkItemResult = z.infer<typeof BulkItemResult>;

/** Réponse de `bulk-status` et `bulk-assign` : un résultat par id, dans l'ordre de la requête (dédoublonné). */
export const BulkResult = z.object({ results: z.array(BulkItemResult) });
export type BulkResult = z.infer<typeof BulkResult>;

export const CommentCreate = z.object({ body: z.string().trim().min(1).max(COMMENT_MAX_LENGTH) });
export type CommentCreate = z.infer<typeof CommentCreate>;

export const AssignBody = z.object({ ownerId: Id.nullable() });
export type AssignBody = z.infer<typeof AssignBody>;

/** `POST /opportunities/:id/overrides` : `value` 0–10 (stockée /10), rationale ≥ 10 caractères. */
export const OverrideCreate = z.object({
  axis: OverrideAxis,
  value: z.number().min(0).max(10),
  rationale: z.string().trim().min(OVERRIDE_RATIONALE_MIN_LENGTH).max(RATIONALE_MAX_LENGTH),
});
export type OverrideCreate = z.infer<typeof OverrideCreate>;

export const OverrideDisable = z.object({ rationale: Rationale });
export type OverrideDisable = z.infer<typeof OverrideDisable>;

export const UnknownResolve = z.object({ resolution: z.string().trim().min(1).max(2000) });
export type UnknownResolve = z.infer<typeof UnknownResolve>;

/** `POST /clusters/:id/promote` : `productType` par défaut celui de l'analyse ou du triage. */
export const PromoteBody = z.object({
  title: Title,
  canonicalJob: Job,
  targetSegment: Segment,
  productType: ProductType.optional(),
  rationale: Rationale,
});
export type PromoteBody = z.infer<typeof PromoteBody>;

/** `POST /opportunities` : création manuelle sans cluster. */
export const ManualCreateBody = z.object({
  marketId: Id,
  title: Title,
  canonicalJob: Job,
  targetSegment: Segment,
  productType: ProductType,
  intent: Intent.default('task'),
  rationale: Rationale,
});
export type ManualCreateBody = z.infer<typeof ManualCreateBody>;
export type ManualCreateInput = z.input<typeof ManualCreateBody>;
